/**
 * ADCRangeConverterPropertyPanel Component
 *
 * Custom property panel for adc_range_converter workflow nodes.
 * Provides UI for configuring ADC-to-physical value conversion
 * via current sensing (4-20mA, 0-10V, etc.)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Node, Edge } from 'reactflow';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle2,
  Calculator,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { FOREST_GREEN } from '@/styles/tokens';

// ================================
// Types
// ================================

export interface ADCRangeConverterConfig {
  // ADC hardware parameters
  adcMaxVoltage: number;
  resistorOhms: number;

  // Input range (mA)
  inputRangeMin: number;
  inputRangeMax: number;

  // Output range (unit-agnostic)
  outputRangeMin: number;
  outputRangeMax: number;

  // Variables
  sourceVariable: string;
  outputVariable: string;

  // Options
  clampOutput: boolean;
  decimalPlaces: number;
}

interface ADCRangeConverterPropertyPanelProps {
  node: Node;
  nodes: Node[];
  edges: Edge[];
  onChange: (config: Partial<ADCRangeConverterConfig>) => void;
  className?: string;
}

// ================================
// Calculation Helper
// ================================

function calculateConversion(
  rawAdc: number,
  config: Partial<ADCRangeConverterConfig>
): { currentMa: number; outputValue: number } | null {
  const {
    adcMaxVoltage = 3.3,
    resistorOhms = 100,
    inputRangeMin = 4,
    inputRangeMax = 20,
    outputRangeMin = 0,
    outputRangeMax = 100,
    clampOutput = true,
    decimalPlaces = 2,
  } = config;

  if (resistorOhms === 0) return null;

  // Stage 1: ADC → Current (mA)
  const currentMa = ((rawAdc / 255) * adcMaxVoltage / resistorOhms) * 1000;

  // Stage 2: Current → Output (range mapping)
  const inputRange = inputRangeMax - inputRangeMin;
  const outputRange = outputRangeMax - outputRangeMin;

  let outputValue: number;
  if (inputRange === 0) {
    outputValue = outputRangeMin;
  } else {
    const normalized = (currentMa - inputRangeMin) / inputRange;
    outputValue = outputRangeMin + normalized * outputRange;
  }

  // Apply clamping
  if (clampOutput) {
    const minOut = Math.min(outputRangeMin, outputRangeMax);
    const maxOut = Math.max(outputRangeMin, outputRangeMax);
    outputValue = Math.max(minOut, Math.min(maxOut, outputValue));
  }

  // Round to specified decimal places
  const factor = Math.pow(10, decimalPlaces);
  outputValue = Math.round(outputValue * factor) / factor;

  return {
    currentMa: Math.round(currentMa * 100) / 100,
    outputValue,
  };
}

// ================================
// Main Component
// ================================

export const ADCRangeConverterPropertyPanel: React.FC<
  ADCRangeConverterPropertyPanelProps
> = ({ node, onChange, className }) => {
  // Test conversion state
  const [testRawValue, setTestRawValue] = useState<string>('111');
  const [testResult, setTestResult] = useState<{
    currentMa: number;
    outputValue: number;
  } | null>(null);
  const [showTestSection, setShowTestSection] = useState(false);

  // Default configuration values
  const defaultConfig: ADCRangeConverterConfig = {
    adcMaxVoltage: 3.3,
    resistorOhms: 100,
    inputRangeMin: 4,
    inputRangeMax: 20,
    outputRangeMin: 0,
    outputRangeMax: 100,
    sourceVariable: '',
    outputVariable: 'convertedValue',
    clampOutput: true,
    decimalPlaces: 2,
  };

  // Extract config from node data with defaults
  const config: ADCRangeConverterConfig = {
    ...defaultConfig,
    ...(node.data?.config || {}),
  };

  // Initialize defaults when config is empty (on first mount)
  useEffect(() => {
    const nodeConfig = node.data?.config || {};
    // Check if essential numeric fields are missing
    if (
      nodeConfig.adcMaxVoltage === undefined ||
      nodeConfig.resistorOhms === undefined ||
      nodeConfig.inputRangeMin === undefined
    ) {
      // Initialize with all defaults
      onChange(defaultConfig);
    }
  }, []); // Run only once on mount

  // Handler for number inputs
  const handleNumberChange = useCallback(
    (field: keyof ADCRangeConverterConfig, value: string) => {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        onChange({ [field]: numValue });
      } else if (value === '' || value === '-') {
        // Allow empty or minus sign for editing
        onChange({ [field]: value as any });
      }
    },
    [onChange]
  );

  // Handler for string inputs
  const handleStringChange = useCallback(
    (field: keyof ADCRangeConverterConfig, value: string) => {
      onChange({ [field]: value });
    },
    [onChange]
  );

  // Handler for boolean inputs
  const handleBooleanChange = useCallback(
    (field: keyof ADCRangeConverterConfig, checked: boolean) => {
      onChange({ [field]: checked });
    },
    [onChange]
  );

  // Test conversion
  const handleTestConversion = useCallback(() => {
    const rawAdc = parseFloat(testRawValue);
    if (isNaN(rawAdc)) {
      setTestResult(null);
      return;
    }
    const result = calculateConversion(rawAdc, config);
    setTestResult(result);
  }, [testRawValue, config]);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold mb-1 flex items-center gap-2">
          <Calculator className="h-4 w-4" style={{ color: FOREST_GREEN.DEFAULT }} />
          ADC Range Converter
        </h3>
        <p className="text-xs text-muted-foreground">
          Convert ADC readings to physical values via current sensing
        </p>
      </div>

      {/* ADC Hardware Section */}
      <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          ADC Hardware
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="adcMaxVoltage" className="text-xs">
              Max Voltage (V)
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="adcMaxVoltage"
              type="number"
              step="0.1"
              min="0"
              value={config.adcMaxVoltage}
              onChange={(e) => handleNumberChange('adcMaxVoltage', e.target.value)}
              className="h-8 text-sm"
              placeholder="3.3"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="resistorOhms" className="text-xs">
              Resistor (Ohms)
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="resistorOhms"
              type="number"
              step="1"
              min="1"
              value={config.resistorOhms}
              onChange={(e) => handleNumberChange('resistorOhms', e.target.value)}
              className="h-8 text-sm"
              placeholder="100"
            />
          </div>
        </div>
      </div>

      {/* Input Range Section */}
      <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Input Range (mA)
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="inputRangeMin" className="text-xs">
              Min
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="inputRangeMin"
              type="number"
              step="0.1"
              value={config.inputRangeMin}
              onChange={(e) => handleNumberChange('inputRangeMin', e.target.value)}
              className="h-8 text-sm"
              placeholder="4"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="inputRangeMax" className="text-xs">
              Max
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="inputRangeMax"
              type="number"
              step="0.1"
              value={config.inputRangeMax}
              onChange={(e) => handleNumberChange('inputRangeMax', e.target.value)}
              className="h-8 text-sm"
              placeholder="20"
            />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Common: 4-20 mA sensors use min=4, max=20
        </p>
      </div>

      {/* Output Range Section */}
      <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Output Range
        </h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="outputRangeMin" className="text-xs">
              Min
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="outputRangeMin"
              type="number"
              step="0.01"
              value={config.outputRangeMin}
              onChange={(e) => handleNumberChange('outputRangeMin', e.target.value)}
              className="h-8 text-sm"
              placeholder="0"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="outputRangeMax" className="text-xs">
              Max
              <span className="text-red-500 ml-0.5">*</span>
            </Label>
            <Input
              id="outputRangeMax"
              type="number"
              step="0.01"
              value={config.outputRangeMax}
              onChange={(e) => handleNumberChange('outputRangeMax', e.target.value)}
              className="h-8 text-sm"
              placeholder="6.56"
            />
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Output in your desired units (e.g., 0-6.56 for feet, 0-100 for %)
        </p>
      </div>

      {/* Variables Section */}
      <div className="space-y-3">
        <div className="space-y-1">
          <Label htmlFor="sourceVariable" className="text-xs">
            Source Variable
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Input
            id="sourceVariable"
            value={config.sourceVariable}
            onChange={(e) => handleStringChange('sourceVariable', e.target.value)}
            className="h-8 text-sm"
            placeholder="e.g., WaterLevelRaw"
          />
          <p className="text-[10px] text-muted-foreground">
            Variable containing the raw ADC value (0-255)
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="outputVariable" className="text-xs">
            Output Variable
            <span className="text-red-500 ml-0.5">*</span>
          </Label>
          <Input
            id="outputVariable"
            value={config.outputVariable}
            onChange={(e) => handleStringChange('outputVariable', e.target.value)}
            className="h-8 text-sm"
            placeholder="e.g., waterDepthFeet"
          />
          <p className="text-[10px] text-muted-foreground">
            Variable name to store the converted result
          </p>
        </div>
      </div>

      {/* Options Section */}
      <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
        <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Options
        </h4>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="clampOutput"
            checked={config.clampOutput}
            onCheckedChange={(checked) =>
              handleBooleanChange('clampOutput', checked as boolean)
            }
          />
          <Label htmlFor="clampOutput" className="text-xs cursor-pointer">
            Clamp to output range
          </Label>
        </div>
        <div className="space-y-1">
          <Label htmlFor="decimalPlaces" className="text-xs">
            Decimal Places
          </Label>
          <Input
            id="decimalPlaces"
            type="number"
            min="0"
            max="10"
            step="1"
            value={config.decimalPlaces}
            onChange={(e) => handleNumberChange('decimalPlaces', e.target.value)}
            className="h-8 text-sm w-20"
            placeholder="2"
          />
        </div>
      </div>

      {/* Test Conversion Section */}
      <Collapsible open={showTestSection} onOpenChange={setShowTestSection}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-between h-8 px-2"
          >
            <span className="flex items-center gap-2 text-xs">
              <Play className="h-3 w-3" />
              Test Conversion
            </span>
            {showTestSection ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-3">
          <div className="p-3 bg-muted/50 rounded-lg space-y-3">
            <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
              <span className="px-1.5 py-0.5 bg-muted rounded font-mono">
                0-255
              </span>
              <span>ADC value expected</span>
            </div>

            <div className="space-y-1">
              <Label htmlFor="testRawValue" className="text-xs">
                Raw ADC Value
              </Label>
              <Input
                id="testRawValue"
                type="number"
                min="0"
                max="255"
                value={testRawValue}
                onChange={(e) => setTestRawValue(e.target.value)}
                className="h-8 text-sm font-mono"
                placeholder="e.g., 111"
              />
            </div>

            <Button
              size="sm"
              onClick={handleTestConversion}
              className="w-full h-7 text-xs"
            >
              <Play className="h-3 w-3 mr-1" />
              Test Conversion
            </Button>

            {testResult && (
              <div className="p-2 bg-background rounded border space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2
                    className="h-4 w-4"
                    style={{ color: FOREST_GREEN.DEFAULT }}
                  />
                  <span className="text-xs font-medium">Result</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex flex-col">
                    <span className="text-muted-foreground">Current</span>
                    <span className="font-mono font-medium">
                      {testResult.currentMa} mA
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-muted-foreground">Output</span>
                    <span className="font-mono font-medium text-lg">
                      {testResult.outputValue}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default ADCRangeConverterPropertyPanel;
